import { useContext, useEffect, useMemo, useState } from 'react'

import {
  BabyGrowthCardEmbedProps,
  BabyVaccineCardEmbed,
  BabyVaccineForm,
  BabyVaccineFormProps,
  BabyVaccineFormValue,
  useHealthToolsCache,
} from '@hhgtech/hhg-components'
import { QUERY_HEALTH_TOOL } from '@hhgtech/hhg-components/constants'
import { getURLwithSSOTracking } from '@hhgtech/hhg-components/ssoV2'
import dayjs from 'dayjs'

import { callApiNew } from 'api/axios'
import { formatBabyVaccinationContent } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT, basePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { BabyVaccinationContent } from 'interfaces/types/BabyVaccination'
import { AppContext } from 'state/appContext'
import { formatGenderUrl } from 'utils/format'

import { HealthToolCardEmbedProps } from '../types'

export type InputError = {
  age: string
  name: string
}

type CommonProps = HealthToolCardEmbedProps

export type BabyVaccineCardWrapperProps =
  | ({ onlyUseForm: true } & Partial<BabyVaccineFormProps> & CommonProps)
  | ({ onlyUseForm?: false } & Partial<BabyGrowthCardEmbedProps> & CommonProps)

export const BabyVaccinationCard = ({
  isSide,
  onlyUseForm,
  ...props
}: BabyVaccineCardWrapperProps) => {
  const { locale, router } = useRouterExtend()
  const [data, setData] = useState<BabyVaccinationContent | null>(null)

  const { state } = useContext(AppContext) || {}
  const { getHealthToolLink } = useHealthToolsCache(locale)
  const toolLink = useMemo(
    () => getHealthToolLink(HEALTH_TOOL_TYPE.BABY_VACCINE),
    [getHealthToolLink],
  )

  const { isLoggedIn } = state || {}

  useEffect(() => {
    ;(async () => {
      const res = await callApiNew(
        PATHS.BABY_VACCINE.GET_HEALTH_TOOL_BABY_VACCINE_INFO,
      )
      setData(
        () => formatBabyVaccinationContent(res?.data) as BabyVaccinationContent,
      )
    })()
  }, [])

  const onSubmit = (values: BabyVaccineFormValue) => {
    const { gender = '' } = values
    const age = dayjs(values.age).format(API_DATE_FORMAT)

    ;(async () => {
      const redirectPageUrl = toolLink

      const destination = `${
        typeof window !== 'undefined' ? window.location.origin : ''
      }${basePath}${redirectPageUrl}?${
        QUERY_HEALTH_TOOL.gender
      }=${formatGenderUrl(gender)}&${QUERY_HEALTH_TOOL.age}=${age}`

      if (!isLoggedIn) {
        redirectToSSOWithReturnUrl(destination, 'baby-vaccine')
      } else {
        router.push(
          getURLwithSSOTracking(destination, { source: 'baby-vaccine' })
            ?.href || destination,
        )
      }
    })()
  }

  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()

  if (!toolLink)
    return (
      <div
        style={{
          height: 500,
        }}
      ></div>
    )

  return onlyUseForm ? (
    <BabyVaccineForm
      formLayout={isMobile || isSide ? 'default' : 'inline'}
      onSubmit={onSubmit}
      submitProps={{
        dataEventLabel: currentUrl,
      }}
      {...props}
    />
  ) : (
    <BabyVaccineCardEmbed
      babyVaccineContent={data}
      formProps={
        {
          formLayout: isMobile || isSide ? 'default' : 'inline',
          onSubmit,
          submitProps: {
            dataEventLabel: currentUrl,
          },
        } as any
      }
      {...props}
    />
  )
}
